import { Drawer, Typography, Space, Tooltip } from 'antd';
import {
  ExperimentOutlined,
  SafetyOutlined,
  WarningFilled,
  CloseCircleFilled,
  InfoCircleOutlined,
  BookOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { colors } from '@/theme/tokens';
import {
  LINT_SEVERITY,
  type LintFinding,
  type LintSeverity,
  type LintSeverityDescriptor,
  type LintRuleCatalogEntry,
} from '@/api/instructionLint';

const { Text, Title, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// RuleDetailDrawer — "what is this rule, and why does it exist?"
// ─────────────────────────────────────────────────────────────────────────────
//
// PURPOSE
//   The lint findings card shows WHAT failed (rule code + finding text +
//   suggested fix). This drawer answers WHY the rule exists in the first
//   place — the failure mode it prevents, told as a story, plus an example
//   of compliant phrasing from a passing agent.
//
// REUSE CONTRACT
//   The drawer renders a slim `RuleDetail` shape (defined below), not a
//   `LintFinding`. Two surfaces feed this same shape:
//     • Instruction Lint page — adapts a `LintFinding` (has severity context)
//     • Rule Library page — adapts a `LintRuleCatalogEntry` (no severity)
//   When `severity` is null, the drawer skips the severity pill in the chips
//   row and renders the severity scale without an active highlight.
//
//   Keeping the drawer prop slim (vs taking either type and switching) means
//   future surfaces can feed it too — e.g. a per-rule detail page when
//   workstream 6 lands. (lint-ux-evolution.md §3 v1.1.)
// ─────────────────────────────────────────────────────────────────────────────

export interface RuleDetail {
  ruleId: string;
  title: string;
  category: string;
  /** One-line popover for the category chip; null falls back to a generic message. */
  categoryDescription: string | null;
  /** Null on catalog views (no agent context yet). */
  severity: LintSeverity | null;
  /** Null on catalog views (no agent context yet). */
  severityDescriptor: LintSeverityDescriptor | null;
  whatItChecks: string | null;
  whyItExists: string | null;
  compliantSnippetMarkdown: string | null;
}

// ─── Adapters — keep the drawer pure; surfaces convert to RuleDetail here ───

export function ruleDetailFromFinding(f: LintFinding): RuleDetail {
  return {
    ruleId: f.ruleId,
    title: f.title,
    category: f.category,
    categoryDescription: f.categoryDescription,
    severity: f.severity,
    severityDescriptor: f.severityDescriptor,
    whatItChecks: f.whatItChecks,
    whyItExists: f.whyItExists,
    compliantSnippetMarkdown: f.compliantSnippetMarkdown,
  };
}

export function ruleDetailFromCatalog(e: LintRuleCatalogEntry): RuleDetail {
  return {
    ruleId: e.id,
    title: e.title,
    category: e.category,
    categoryDescription: e.categoryDescription,
    severity: null,
    severityDescriptor: null,
    whatItChecks: e.whatItChecks,
    whyItExists: e.whyItExists,
    compliantSnippetMarkdown: e.compliantSnippetMarkdown,
  };
}

// ─── Drawer ─────────────────────────────────────────────────────────────────

interface Props {
  /** When non-null, drawer is open and showing this rule's detail. */
  detail: RuleDetail | null;
  onClose: () => void;
  isDark: boolean;
  /**
   * When false, hide the footer "Open in Rules catalog" link — the drawer
   * is already on the catalog page so the link would be circular.
   * Default true (lint findings page).
   */
  showCatalogLink?: boolean;
}

export function RuleDetailDrawer({
  detail,
  onClose,
  isDark,
  showCatalogLink = true,
}: Props) {
  const open = detail !== null;

  // Esc closes — antd's Drawer handles Esc by default, but we wire it
  // explicitly so the focus ring inside the drawer doesn't swallow the key.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!detail) {
    // Always render the drawer so antd can animate in/out cleanly. When
    // there's no rule selected we render a closed shell with no body.
    return (
      <Drawer
        open={false}
        onClose={onClose}
        width="min(1080px, 80vw)"
        title={null}
        destroyOnClose
      />
    );
  }

  const categoryStyle = categoryPalette(detail.category, isDark);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="min(1080px, 80vw)"
      destroyOnClose
      title={
        <Space size={8} align="center" wrap>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              padding: '3px 9px',
              borderRadius: 4,
              background: isDark ? colors.slate[700] : colors.slate[100],
              color: isDark ? colors.slate[200] : colors.slate[700],
              fontWeight: 600,
            }}
          >
            {detail.ruleId}
          </span>
          <span
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: isDark ? colors.slate[50] : colors.slate[900],
            }}
          >
            {detail.title}
          </span>
        </Space>
      }
      styles={{
        body: {
          padding: 0,
          background: isDark ? colors.slate[900] : colors.slate[50],
        },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* ── Chips row — category + (optional) severity ──────────────── */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            padding: '14px 20px 14px',
          }}
        >
          <Tooltip
            title={
              detail.categoryDescription ??
              `Authoring rules grouped under "${detail.category}".`
            }
            mouseEnterDelay={0.1}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 500,
                padding: '3px 10px',
                borderRadius: 999,
                background: categoryStyle.bg,
                color: categoryStyle.fg,
                cursor: 'help',
              }}
            >
              {categoryStyle.icon}
              {detail.category}
            </span>
          </Tooltip>
          {detail.severity != null && (
            <SeverityPill
              descriptor={detail.severityDescriptor}
              severity={detail.severity}
            />
          )}
        </div>

        {/* ── Two-column body. Wraps to single column when the drawer is
              narrower than ~800px (mobile / split-screen). Each column is
              `flex: 1 1 380px` so they share remaining space evenly when both
              fit, and stack cleanly when they don't. ─────────────────── */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          }}
        >
          {/* ── LEFT column: rationale (the WHY) ─────────────────────── */}
          <div
            style={{
              flex: '1 1 380px',
              minWidth: 0,
              borderRight: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            }}
          >
            {detail.whatItChecks && (
              <Section title="What this rule checks" isDark={isDark} firstInColumn>
                <Paragraph
                  style={{
                    margin: 0,
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: isDark ? colors.slate[100] : colors.slate[800],
                  }}
                >
                  {detail.whatItChecks}
                </Paragraph>
              </Section>
            )}

            {detail.whyItExists && (
              <Section title="Why this rule exists" isDark={isDark}>
                <div
                  className="rule-detail-prose"
                  style={{
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: isDark ? colors.slate[200] : colors.slate[700],
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {detail.whyItExists}
                  </ReactMarkdown>
                </div>
              </Section>
            )}
          </div>

          {/* ── RIGHT column: example + severity scale ──────────────── */}
          <div style={{ flex: '1 1 380px', minWidth: 0 }}>
            {detail.compliantSnippetMarkdown && (
              <Section title="What good looks like" isDark={isDark} firstInColumn>
                <Text
                  type="secondary"
                  italic
                  style={{
                    fontSize: 11.5,
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  Anonymized example — structure preserved, identifying details removed.
                </Text>
                <div
                  style={{
                    background: isDark ? colors.slate[800] : '#FFFFFF',
                    border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                    borderRadius: 6,
                    padding: '12px 16px',
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: isDark ? colors.slate[100] : colors.slate[800],
                  }}
                >
                  <div className="rule-detail-prose">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {detail.compliantSnippetMarkdown}
                    </ReactMarkdown>
                  </div>
                </div>
              </Section>
            )}

            <Section
              title="Severity scale"
              isDark={isDark}
              firstInColumn={!detail.compliantSnippetMarkdown}
            >
              <SeverityScale active={detail.severity} isDark={isDark} />
              {detail.severityDescriptor?.longDescription && (
                <Paragraph
                  style={{
                    margin: '12px 0 0 0',
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: isDark ? colors.slate[300] : colors.slate[600],
                  }}
                >
                  {detail.severityDescriptor.longDescription}
                </Paragraph>
              )}
              {detail.severity == null && (
                <Paragraph
                  style={{
                    margin: '12px 0 0 0',
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: isDark ? colors.slate[400] : colors.slate[500],
                    fontStyle: 'italic',
                  }}
                >
                  Severity is determined per-agent at lint time, based on the agent's <Text code>EnforceKbLoad</Text> / <Text code>EnforceSkillLoad</Text> flags.
                </Paragraph>
              )}
            </Section>
          </div>
        </div>

        {/* ── Footer: link to canonical Rule Library ──────────────────── */}
        {showCatalogLink && (
          <div
            style={{
              padding: '14px 20px 24px',
              borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              background: isDark ? colors.slate[900] : colors.slate[50],
            }}
          >
            <Link
              to={`/platform/rules#${encodeURIComponent(detail.ruleId)}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: colors.violet[600],
                fontWeight: 500,
              }}
              onClick={onClose}
            >
              <BookOutlined />
              Open in Rule Library
              <ArrowRightOutlined />
            </Link>
          </div>
        )}
      </div>
    </Drawer>
  );
}

// ─── Section wrapper — uniform headings + spacing across the drawer ─────────

function Section({
  title,
  isDark,
  children,
  firstInColumn = false,
}: {
  title: string;
  isDark: boolean;
  children: React.ReactNode;
  /**
   * When true, the section omits its top border — used for the first
   * section in each column of the two-column layout, where the column's
   * shared top divider already provides the visual separator.
   */
  firstInColumn?: boolean;
}) {
  return (
    <section
      style={{
        padding: '14px 20px',
        borderTop: firstInColumn
          ? 'none'
          : `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
      }}
    >
      <Title
        level={5}
        style={{
          margin: '0 0 8px 0',
          fontSize: 11,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: isDark ? colors.slate[400] : colors.slate[500],
          fontWeight: 600,
        }}
      >
        {title}
      </Title>
      {children}
    </section>
  );
}

// ─── Severity pill — recolored severity tag for the drawer chips row ────────

function SeverityPill({
  descriptor,
  severity,
}: {
  descriptor: LintSeverityDescriptor | null;
  severity: LintSeverity;
}) {
  const tone = severityFg(severity);
  const label = descriptor?.label ?? severityLabelFallback(severity);
  const Icon =
    severity === LINT_SEVERITY.Block
      ? CloseCircleFilled
      : severity === LINT_SEVERITY.Warn
        ? WarningFilled
        : InfoCircleOutlined;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        padding: '3px 10px',
        borderRadius: 999,
        background: `${tone}1A`,
        color: tone,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      <Icon style={{ fontSize: 13 }} aria-hidden />
      {label}
    </span>
  );
}

// ─── Severity scale graphic — ▮▮▮ / ▮▮ / ▮ with active row highlighted ─────

function SeverityScale({
  active,
  isDark,
}: {
  active: LintSeverity | null;
  isDark: boolean;
}) {
  const rows: { sev: LintSeverity; label: string; bars: number; tone: string }[] = [
    { sev: LINT_SEVERITY.Block, label: 'Blocking', bars: 3, tone: colors.red[600] },
    { sev: LINT_SEVERITY.Warn, label: 'Advisory', bars: 2, tone: colors.amber[600] },
    { sev: LINT_SEVERITY.Info, label: 'Info', bars: 1, tone: colors.ink[500] },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((r) => {
        const isActive = active != null && r.sev === active;
        return (
          <div
            key={r.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '6px 10px',
              borderRadius: 6,
              background: isActive
                ? `${r.tone}14`
                : isDark
                  ? colors.slate[800]
                  : '#FFFFFF',
              border: `1px solid ${isActive ? r.tone : isDark ? colors.slate[700] : colors.slate[200]}`,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                gap: 2,
                width: 36,
              }}
              aria-hidden
            >
              {[1, 2, 3].map((i) => (
                <span
                  key={i}
                  style={{
                    width: 8,
                    height: 14,
                    borderRadius: 2,
                    background: i <= r.bars ? r.tone : isDark ? colors.slate[700] : colors.slate[200],
                  }}
                />
              ))}
            </span>
            <strong
              style={{
                fontSize: 12.5,
                color: isActive ? r.tone : isDark ? colors.slate[200] : colors.slate[700],
                minWidth: 70,
              }}
            >
              {r.label}
            </strong>
            <span
              style={{
                fontSize: 12,
                color: isDark ? colors.slate[300] : colors.slate[600],
                lineHeight: 1.5,
              }}
            >
              {shortFor(r.sev)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function severityFg(s: LintSeverity): string {
  if (s === LINT_SEVERITY.Block) return colors.red[600];
  if (s === LINT_SEVERITY.Warn) return colors.amber[600];
  return colors.ink[500];
}

function severityLabelFallback(s: LintSeverity): string {
  if (s === LINT_SEVERITY.Block) return 'Blocking';
  if (s === LINT_SEVERITY.Warn) return 'Advisory';
  return 'Info';
}

function shortFor(s: LintSeverity): string {
  if (s === LINT_SEVERITY.Block) return 'Save disabled until resolved (only when EnforceX is on).';
  if (s === LINT_SEVERITY.Warn) return 'Save proceeds. Yellow advisory; addressable later.';
  return 'Telemetry only — a nudge, not a gate.';
}

function categoryPalette(
  category: string,
  isDark: boolean,
): { icon: React.ReactNode; fg: string; bg: string } {
  const map: Record<string, { icon: React.ReactNode; fg: string; bg: string }> = {
    Grounding: {
      icon: <ExperimentOutlined aria-hidden />,
      fg: colors.violet[700],
      bg: isDark ? `${colors.violet[700]}22` : colors.violet[100],
    },
    'Clinical safety': {
      icon: <SafetyOutlined aria-hidden />,
      fg: colors.red[700],
      bg: isDark ? `${colors.red[700]}22` : colors.red[100],
    },
    'Anti-patterns': {
      icon: <WarningFilled aria-hidden />,
      fg: colors.amber[700],
      bg: isDark ? `${colors.amber[700]}22` : colors.amber[100],
    },
  };
  return (
    map[category] ?? {
      icon: null,
      fg: isDark ? colors.slate[300] : colors.slate[600],
      bg: isDark ? colors.slate[700] : colors.slate[100],
    }
  );
}
